// react_frontend/src/Components/Modules/Accounting/JournalEntries.js
import React, { useState, useEffect } from 'react';
import { AxiosAuthInstance } from '../../../AxiosInterceptors';
import Breadcrumb from '../../Partials/Breadcrumb';
import {
    Box, Typography, Paper, Button, IconButton, Tooltip, TextField,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Dialog, DialogTitle, DialogContent, DialogActions, Chip,
    FormControl, InputLabel, Select, MenuItem, IconButton as MuiIconButton,
} from '@mui/material';
import { FaPlus, FaEdit, FaTrash, FaCheck, FaSearch, FaTrashAlt } from 'react-icons/fa';
import Swal from 'sweetalert2';

export default function JournalEntries() {
    const [entries, setEntries] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [lines, setLines] = useState([
        { account_id: '', description: '', debit: 0, credit: 0 },
        { account_id: '', description: '', debit: 0, credit: 0 },
    ]);
    const [form, setForm] = useState({ entry_date: '', description: '' });

    const fetchEntries = async () => {
        setLoading(true);
        try {
            const res = await AxiosAuthInstance.get('journal-entries', { params: { search } });
            setEntries(res.data.entries || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchAccounts = async () => {
        try {
            const res = await AxiosAuthInstance.get('journal-entries/accounts');
            setAccounts(res.data.accounts || []);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        fetchEntries();
        fetchAccounts();
    }, [search]);

    const openDialog = (entry = null) => {
        if (entry) {
            setEditing(entry);
            setForm({ entry_date: entry.entry_date, description: entry.description || '' });
            setLines(entry.lines?.map(l => ({
                account_id: l.account_id,
                description: l.description || '',
                debit: parseFloat(l.debit) || 0,
                credit: parseFloat(l.credit) || 0,
            })) || [
                { account_id: '', description: '', debit: 0, credit: 0 },
                { account_id: '', description: '', debit: 0, credit: 0 },
            ]);
        } else {
            setEditing(null);
            setForm({ entry_date: new Date().toISOString().split('T')[0], description: '' });
            setLines([
                { account_id: '', description: '', debit: 0, credit: 0 },
                { account_id: '', description: '', debit: 0, credit: 0 },
            ]);
        }
        setDialogOpen(true);
    };

    const addLine = () => {
        setLines([...lines, { account_id: '', description: '', debit: 0, credit: 0 }]);
    };

    const removeLine = (index) => {
        if (lines.length <= 2) return;
        setLines(lines.filter((_, i) => i !== index));
    };

    const updateLine = (index, field, value) => {
        const newLines = [...lines];
        newLines[index] = { ...newLines[index], [field]: value };
        setLines(newLines);
    };

    const totalDebit = lines.reduce((sum, l) => sum + (parseFloat(l.debit) || 0), 0);
    const totalCredit = lines.reduce((sum, l) => sum + (parseFloat(l.credit) || 0), 0);
    const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;

    const handleSave = async () => {
        if (!isBalanced) {
            Swal.fire({ icon: 'error', title: 'Not Balanced', text: 'Debit and Credit must be equal' });
            return;
        }

        try {
            const payload = { ...form, lines };
            const url = editing ? `journal-entries/${editing.id}` : 'journal-entries';
            const method = editing ? 'put' : 'post';
            const res = await AxiosAuthInstance[method](url, payload);

            if (res.data.status) {
                Swal.fire({ icon: 'success', title: 'Success!', timer: 1500, showConfirmButton: false });
                setDialogOpen(false);
                fetchEntries();
            }
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || 'Failed' });
        }
    };

    const handlePost = async (id) => {
        const result = await Swal.fire({
            title: 'Post Entry?',
            text: 'This action cannot be undone',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#667eea',
        });

        if (result.isConfirmed) {
            try {
                await AxiosAuthInstance.post(`journal-entries/${id}/post`);
                Swal.fire({ icon: 'success', title: 'Posted!', timer: 1500, showConfirmButton: false });
                fetchEntries();
            } catch (err) {
                Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || 'Failed' });
            }
        }
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Delete Entry?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#f44336',
        });

        if (result.isConfirmed) {
            try {
                await AxiosAuthInstance.delete(`journal-entries/${id}`);
                Swal.fire({ icon: 'success', title: 'Deleted!', timer: 1500, showConfirmButton: false });
                fetchEntries();
            } catch (err) {
                Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || 'Failed' });
            }
        }
    };

    return (
        <Box sx={{ p: 3, bgcolor: '#f5f7fa', minHeight: '100vh' }}>
            <Breadcrumb title="Journal Entries" />

            <Paper sx={{ p: 3, mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5" fontWeight={700}>Journal Entries</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField size="small" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)}
                        InputProps={{ startAdornment: <FaSearch /> }} />
                    <Button variant="contained" startIcon={<FaPlus />} onClick={() => openDialog()}>New Entry</Button>
                </Box>
            </Paper>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#fafafa' }}>
                            <TableCell>Entry #</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell align="right">Debit</TableCell>
                            <TableCell align="right">Credit</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {entries.map((entry) => (
                            <TableRow key={entry.id} hover>
                                <TableCell><code>{entry.entry_number}</code></TableCell>
                                <TableCell>{entry.entry_date}</TableCell>
                                <TableCell>{entry.description || '-'}</TableCell>
                                <TableCell align="right">৳{Number(entry.total_debit).toLocaleString()}</TableCell>
                                <TableCell align="right">৳{Number(entry.total_credit).toLocaleString()}</TableCell>
                                <TableCell>
                                    <Chip label={entry.status} size="small"
                                        sx={{ bgcolor: entry.status === 'posted' ? '#e8f5e9' : '#fff3e0', color: entry.status === 'posted' ? '#2e7d32' : '#ef6c00' }} />
                                </TableCell>
                                <TableCell align="center">
                                    {entry.status === 'draft' && (
                                        <>
                                            <IconButton size="small" onClick={() => openDialog(entry)} sx={{ color: '#1976d2' }}><FaEdit /></IconButton>
                                            <IconButton size="small" onClick={() => handlePost(entry.id)} sx={{ color: '#4caf50' }}><FaCheck /></IconButton>
                                            <IconButton size="small" onClick={() => handleDelete(entry.id)} sx={{ color: '#f44336' }}><FaTrash /></IconButton>
                                        </>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>{editing ? 'Edit Entry' : 'New Journal Entry'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField label="Date" type="date" value={form.entry_date} onChange={(e) => setForm({ ...form, entry_date: e.target.value })} fullWidth InputLabelProps={{ shrink: true }} />
                        <TextField label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} fullWidth />

                        <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2 }}>Lines</Typography>
                        {lines.map((line, index) => (
                            <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center', p: 1, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                                <FormControl size="small" sx={{ flex: 2 }}>
                                    <InputLabel>Account</InputLabel>
                                    <Select value={line.account_id} label="Account" onChange={(e) => updateLine(index, 'account_id', e.target.value)}>
                                        {accounts.map(a => <MenuItem key={a.id} value={a.id}>{a.account_code} - {a.account_name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                                <TextField size="small" label="Description" value={line.description} onChange={(e) => updateLine(index, 'description', e.target.value)} sx={{ flex: 2 }} />
                                <TextField size="small" label="Debit" type="number" value={line.debit} onChange={(e) => updateLine(index, 'debit', e.target.value)} sx={{ flex: 1 }} />
                                <TextField size="small" label="Credit" type="number" value={line.credit} onChange={(e) => updateLine(index, 'credit', e.target.value)} sx={{ flex: 1 }} />
                                <IconButton onClick={() => removeLine(index)} disabled={lines.length <= 2}><FaTrashAlt /></IconButton>
                            </Box>
                        ))}
                        <Button variant="outlined" startIcon={<FaPlus />} onClick={addLine} sx={{ alignSelf: 'flex-start' }}>Add Line</Button>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2, bgcolor: isBalanced ? '#e8f5e9' : '#ffebee', borderRadius: 1 }}>
                            <Typography variant="subtitle1" fontWeight={700}>Total Debit: ৳{totalDebit.toLocaleString()}</Typography>
                            <Typography variant="subtitle1" fontWeight={700}>Total Credit: ৳{totalCredit.toLocaleString()}</Typography>
                            <Typography variant="subtitle1" fontWeight={700} color={isBalanced ? 'success.main' : 'error.main'}>
                                {isBalanced ? '✓ Balanced' : '✗ Not Balanced'}
                            </Typography>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave} disabled={!isBalanced}>Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}